import React, { useEffect, useState, useCallback } from "react";
import { DownloadSimple, Bell, X, ShareNetwork, PlusSquare, AppleLogo, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isPushSupported, pushPermission, getSubscription, enablePush } from "../lib/push";

/**
 * Onboarding prompts — show automatically after login:
 *  1. PWA install dialog (auto-pops when browser fires `beforeinstallprompt`,
 *     or on iOS Safari with manual instructions).
 *  2. Notification permission dialog (after install or if already installed),
 *     so the user grants push permission without hunting for a settings page.
 *
 * Dismissals are stored in localStorage with a TTL so we don't pester users:
 *  - install dismissed → re-ask after 7 days.
 *  - push dismissed → re-ask after 14 days.
 *  - "Don't ask again" (denied at OS level) → never re-ask from us.
 */

const LS_INSTALL_KEY = "rc.onboarding.install.dismissedAt";
const LS_PUSH_KEY = "rc.onboarding.push.dismissedAt";
const INSTALL_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7d
const PUSH_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14d
const INITIAL_DELAY_MS = 1500; // small delay so it doesn't feel jarring on first paint

function recentlyDismissed(key, ttl) {
  try {
    const v = localStorage.getItem(key);
    if (!v) return false;
    return Date.now() - parseInt(v, 10) < ttl;
  } catch { return false; }
}

function stampDismiss(key) {
  try { localStorage.setItem(key, String(Date.now())); } catch { /* ignore */ }
}

export default function OnboardingPrompts() {
  const [deferred, setDeferred] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showPush, setShowPush] = useState(false);
  const [busy, setBusy] = useState(false);

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator?.standalone === true);
  const isIOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isSafari =
    typeof navigator !== "undefined" &&
    /Safari/.test(navigator.userAgent) &&
    !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);

  const maybeShowPush = useCallback(async () => {
    if (!isPushSupported()) return;
    if (pushPermission() !== "default") return; // already granted or denied
    if (recentlyDismissed(LS_PUSH_KEY, PUSH_TTL_MS)) return;
    const sub = await getSubscription();
    if (sub) return; // already subscribed somehow
    setShowPush(true);
  }, []);

  // ---- Install prompt detection ----
  useEffect(() => {
    if (isStandalone) {
      // already installed → skip directly to push prompt
      const t = setTimeout(() => maybeShowPush(), INITIAL_DELAY_MS);
      return () => clearTimeout(t);
    }

    const dismissed = recentlyDismissed(LS_INSTALL_KEY, INSTALL_TTL_MS);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!dismissed) {
        setTimeout(() => setShowInstall(true), INITIAL_DELAY_MS);
      }
    };
    const onInstalled = () => {
      setShowInstall(false);
      setDeferred(null);
      toast.success("App instalada en este dispositivo");
      // After install, ask for push permission too
      setTimeout(() => maybeShowPush(), 800);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt → show manual instructions modal
    let iosTimer;
    if (isIOS && isSafari && !dismissed) {
      iosTimer = setTimeout(() => setShowInstall(true), INITIAL_DELAY_MS);
    }

    // Fallback: if no install event arrives in 4s and not iOS, proceed to push prompt
    const pushFallback = setTimeout(() => {
      // If install modal is already showing, push will be triggered after install/dismiss
      if (!showInstall) maybeShowPush();
    }, 4000);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
      clearTimeout(pushFallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Actions ----
  const acceptInstall = async () => {
    if (deferred) {
      try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice?.outcome === "accepted") {
          // `appinstalled` will fire → push prompt triggered there
          setShowInstall(false);
        } else {
          // user rejected the OS-level dialog
          stampDismiss(LS_INSTALL_KEY);
          setShowInstall(false);
          setTimeout(() => maybeShowPush(), 500);
        }
      } catch {
        setShowInstall(false);
      }
      setDeferred(null);
    } else {
      // iOS or no deferred event — just close the instructions
      setShowInstall(false);
      setTimeout(() => maybeShowPush(), 500);
    }
  };

  const skipInstall = () => {
    stampDismiss(LS_INSTALL_KEY);
    setShowInstall(false);
    setTimeout(() => maybeShowPush(), 500);
  };

  const acceptPush = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Notificaciones activadas");
      setShowPush(false);
    } catch (e) {
      if (e?.message === "Permiso denegado") {
        // user blocked at OS level — don't ask again
        stampDismiss(LS_PUSH_KEY);
        toast.error("Notificaciones bloqueadas. Habilítalas en la configuración del navegador.");
      } else {
        toast.error(e?.message || "No se pudo activar");
      }
      setShowPush(false);
    } finally { setBusy(false); }
  };

  const skipPush = () => {
    stampDismiss(LS_PUSH_KEY);
    setShowPush(false);
  };

  if (!showInstall && !showPush) return null;

  return (
    <>
      {showInstall && (
        <InstallModal
          onAccept={acceptInstall}
          onSkip={skipInstall}
          isIOS={isIOS && isSafari}
        />
      )}
      {showPush && !showInstall && (
        <PushModal onAccept={acceptPush} onSkip={skipPush} busy={busy} />
      )}
    </>
  );
}

function InstallModal({ onAccept, onSkip, isIOS }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-up"
      data-testid="auto-install-modal"
    >
      <div
        className="bg-surface w-full sm:max-w-md p-6 border border-hairline shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-forest text-bg flex items-center justify-center">
              <DownloadSimple size={20} weight="bold" />
            </div>
            <div>
              <div className="font-display text-xl font-black leading-tight">Instala Activa</div>
              <div className="label-mono mt-0.5">Accede más rápido desde tu dispositivo</div>
            </div>
          </div>
          <button
            onClick={onSkip}
            className="p-1 hover:bg-bg"
            data-testid="auto-install-close"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        {isIOS ? (
          <>
            <p className="text-sm text-inkMuted mb-5">
              En iPhone, Safari requiere 3 pasos para instalarla como app:
            </p>
            <ol className="space-y-3 text-sm mb-6">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 bg-forest text-bg font-mono text-xs flex items-center justify-center flex-shrink-0">1</span>
                <div>
                  Toca el botón <b>Compartir</b> <ShareNetwork size={12} weight="bold" className="inline mb-0.5" /> en la barra inferior.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 bg-forest text-bg font-mono text-xs flex items-center justify-center flex-shrink-0">2</span>
                <div>
                  Elige <b>Agregar a pantalla de inicio</b> <PlusSquare size={12} weight="bold" className="inline mb-0.5" />.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 bg-forest text-bg font-mono text-xs flex items-center justify-center flex-shrink-0">3</span>
                <div>
                  Toca <b>Agregar</b> en la esquina superior derecha.
                </div>
              </li>
            </ol>
            <div className="flex gap-2">
              <button
                onClick={onSkip}
                className="flex-1 h-11 border border-hairline text-sm font-semibold hover:bg-bg"
                data-testid="auto-install-skip"
              >
                Más tarde
              </button>
              <button
                onClick={onAccept}
                className="flex-1 h-11 bg-forest text-bg text-sm font-semibold hover:bg-forest-hover inline-flex items-center justify-center gap-2"
                data-testid="auto-install-ios-ok"
              >
                <AppleLogo size={14} weight="fill" /> Entendido
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-inkMuted mb-5">
              Instala la app en tu dispositivo para acceder con un toque, recibir notificaciones y usarla a pantalla completa.
            </p>
            <ul className="space-y-2 text-sm mb-6">
              <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-forest" /> Sin pasar por el navegador</li>
              <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-forest" /> Alertas de chat, créditos y transferencias</li>
              <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-forest" /> Ícono propio en tu pantalla de inicio</li>
            </ul>
            <div className="flex gap-2">
              <button
                onClick={onSkip}
                className="flex-1 h-11 border border-hairline text-sm font-semibold hover:bg-bg"
                data-testid="auto-install-skip"
              >
                Más tarde
              </button>
              <button
                onClick={onAccept}
                className="flex-1 h-11 bg-forest text-bg text-sm font-semibold hover:bg-forest-hover inline-flex items-center justify-center gap-2"
                data-testid="auto-install-accept"
              >
                <DownloadSimple size={14} weight="bold" /> Instalar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PushModal({ onAccept, onSkip, busy }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-up"
      data-testid="auto-push-modal"
    >
      <div className="bg-surface w-full sm:max-w-md p-6 border border-hairline shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-burnt text-bg flex items-center justify-center">
              <Bell size={20} weight="duotone" />
            </div>
            <div>
              <div className="font-display text-xl font-black leading-tight">Activa las notificaciones</div>
              <div className="label-mono mt-0.5">No te pierdas movimientos importantes</div>
            </div>
          </div>
          <button
            onClick={onSkip}
            className="p-1 hover:bg-bg"
            data-testid="auto-push-close"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        <p className="text-sm text-inkMuted mb-4">
          Recibe alertas instantáneas de:
        </p>
        <ul className="space-y-2 text-sm mb-6">
          <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-forest" /> Mensajes nuevos en el chat con tu asesor</li>
          <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-forest" /> Créditos aprobados o rechazados</li>
          <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-forest" /> Transferencias autorizadas o pendientes</li>
        </ul>

        <div className="flex gap-2">
          <button
            onClick={onSkip}
            disabled={busy}
            className="flex-1 h-11 border border-hairline text-sm font-semibold hover:bg-bg disabled:opacity-50"
            data-testid="auto-push-skip"
          >
            Ahora no
          </button>
          <button
            onClick={onAccept}
            disabled={busy}
            className="flex-1 h-11 bg-forest text-bg text-sm font-semibold hover:bg-forest-hover inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="auto-push-accept"
          >
            <Bell size={14} weight="bold" /> {busy ? "Activando…" : "Activar"}
          </button>
        </div>
        <p className="text-[11px] text-inkMuted mt-3 text-center">
          Tu navegador te pedirá confirmación. Puedes cambiarlo cuando quieras desde tu perfil.
        </p>
      </div>
    </div>
  );
}
